/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { vehicleApi } from '../../../api/vehicleApi';
import { useSafeClose } from '../../../hooks/useSafeClose';
import './VehicleStatus.css';

/**
 * VehicleDetailModal Component
 * 
 * Displays detailed information about a vehicle, including deadlines,
 * workshop contacts, and associated documents.
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Callback triggered when the modal should close
 * @param {Object} props.vehicle - The vehicle data to display
 * @param {Function} props.onEdit - Callback to open the edit modal for this vehicle
 * @param {Function} props.onDelete - Callback to delete this vehicle
 * @param {Function} props.onDocUpload - Callback to open the document upload modal
 * @param {Array} [props.categories] - List of vehicle categories
 * @param {Array} [props.companyUsers] - List of users for lookup
 * @returns {JSX.Element|null} The rendered modal or null if not open/missing data
 */
export default function VehicleDetailModal({
    isOpen,
    onClose,
    vehicle,
    onEdit,
    onDelete,
    onDocUpload,
    categories = [],
    companyUsers = []
}) {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTooltip, setActiveTooltip] = useState(null);

    const { overlayRef, onMouseDown, onMouseUp } = useSafeClose(onClose);

    useEffect(() => {
        if (isOpen && vehicle) {
            fetchDocuments();
        }
    }, [isOpen, vehicle]);

    const fetchDocuments = async () => {
        setLoading(true);
        try {
            const data = await vehicleApi.getVehicleDocuments(vehicle.id);
            setDocuments(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen || !vehicle) return null;

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('de-DE');
    };

    const getStatusLabel = (status) => {
        const map = { green: 'Betriebsbereit', yellow: 'Eingeschränkt', red: 'Defekt' };
        return map[status] || status;
    };

    const InfoIcon = ({ type }) => {
        const info = vehicle.workshop_info?.[type];
        if (!info || (!info.company && !info.name)) return null;

        const isVisible = activeTooltip === type;

        return (
            <div style={{ display: 'inline-block', position: 'relative' }}>
                <button 
                    className="info-icon-btn" 
                    onClick={() => setActiveTooltip(isVisible ? null : type)}
                    title="Werkstatt-Info anzeigen"
                >
                    i
                </button>
                {isVisible && (
                    <div className="info-tooltip">
                        <div style={{ fontWeight: 700, marginBottom: '8px', borderBottom: '1px solid #f1f5f9', paddingBottom: '4px' }}>
                            🏥 Werkstatt / Kontakt
                        </div>
                        {info.company && <div className="info-tooltip-item"><span className="info-tooltip-label">Firma:</span> <b>{info.company}</b></div>}
                        {info.name && <div className="info-tooltip-item"><span className="info-tooltip-label">Ansprechp.:</span> {info.name}</div>}
                        {info.address && <div className="info-tooltip-item"><span className="info-tooltip-label">Adresse:</span> {info.address}</div>}
                        {info.phone && <div className="info-tooltip-item"><span className="info-tooltip-label">Telefon:</span> {info.phone}</div>}
                        {info.email && <div className="info-tooltip-item"><span className="info-tooltip-label">E-Mail:</span> {info.email}</div>}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="modal-overlay" ref={overlayRef} onMouseDown={onMouseDown} onMouseUp={onMouseUp}>
            <div className="modal-content vehicle-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header" style={{ alignItems: 'flex-start' }}>
                    <div>
                        <div className="modal-title" style={{ fontSize: '1.4rem' }}>{vehicle.internal_name}</div>
                        <div style={{ color: '#64748b', fontSize: '0.9rem', marginTop: '4px' }}>{vehicle.license_plate}</div>
                    </div>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>

                <div className="modal-body">
                    {/* PROMINENT ACTIONS SECTION */}
                    <div className="modal-section-title">⚡ Schnellaktionen</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', marginBottom: '30px' }}>
                        <button 
                            onClick={() => onDocUpload(vehicle)}
                            style={{ padding: '20px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', cursor: 'pointer', transition: 'all 0.2s', color: '#0f172a', fontWeight: '600' }}
                            onMouseOver={e => e.currentTarget.style.borderColor = '#cbd5e1'}
                            onMouseOut={e => e.currentTarget.style.borderColor = '#e2e8f0'}
                        >
                            <span style={{ fontSize: '24px' }}>📂</span>
                            Dokumente
                        </button>
                        
                        <button 
                            onClick={() => onEdit(vehicle)}
                            style={{ padding: '20px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', cursor: 'pointer', transition: 'all 0.2s', color: '#0f172a', fontWeight: '600' }}
                            onMouseOver={e => e.currentTarget.style.borderColor = '#cbd5e1'}
                            onMouseOut={e => e.currentTarget.style.borderColor = '#e2e8f0'}
                        >
                            <span style={{ fontSize: '24px' }}>✏️</span>
                            Bearbeiten
                        </button>

                        <button 
                            onClick={() => onDelete(vehicle.id)}
                            style={{ padding: '20px', background: '#fff1f2', border: '1px solid #fecaca', borderRadius: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', cursor: 'pointer', transition: 'all 0.2s', color: '#e11d48', fontWeight: '600' }}
                            onMouseOver={e => e.currentTarget.style.borderColor = '#fda4af'}
                            onMouseOut={e => e.currentTarget.style.borderColor = '#fecaca'}
                        >
                            <span style={{ fontSize: '24px' }}>🗑️</span>
                            Fahrzeug Löschen
                        </button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '30px' }}>
                        {/* Linke Spalte: Infos & Fristen */}
                        <div>
                            <div className="modal-section-title">📋 Basis-Informationen</div>
                            <div className="detail-info-summary" style={{ background: '#f8fafc', padding: '15px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                    <div>
                                        <div style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase' }}>Fahrzeugtyp</div>
                                        <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            {vehicle.category_id && categories.find(c => c.id === vehicle.category_id)?.color && (
                                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: categories.find(c => c.id === vehicle.category_id).color }} />
                                            )}
                                            {categories.find(c => c.id === vehicle.category_id)?.name || vehicle.vehicle_type || '—'}
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase' }}>Hersteller / Modell</div>
                                        <div style={{ fontWeight: 600 }}>{vehicle.make || '—'} {vehicle.model || ''}</div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase' }}>VIN / Fahrgestellnummer</div>
                                        <div style={{ fontWeight: 600 }}>{vehicle.vin || '—'}</div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase' }}>Baujahr</div>
                                        <div style={{ fontWeight: 600 }}>{vehicle.year || '—'}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-section-title">📅 Fristen & Kontakte</div>
                            <div className="deadline-details-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                
                                {/* TÜV */}
                                {vehicle.notification_config?.has_tuv !== false && (
                                    <div className="deadline-card-detail">
                                        <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
                                            <span>TÜV</span>
                                            <span className="deadline-date">{formatDate(vehicle.next_tuv)}</span>
                                        </div>
                                        {vehicle.tuv_email && <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '4px' }}>📧 {vehicle.tuv_email}</div>}
                                        {vehicle.workshop_info?.tuv?.company && (
                                            <div style={{ fontSize: '0.8rem', background: 'white', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                                <strong>{vehicle.workshop_info?.tuv?.company}</strong>
                                                {vehicle.workshop_info?.tuv?.phone && <div style={{ fontSize: '0.75rem' }}>📞 {vehicle.workshop_info?.tuv?.phone}</div>}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* INSPECTION */}
                                {vehicle.notification_config?.has_inspection !== false && (
                                    <div className="deadline-card-detail">
                                        <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
                                            <span>Inspektion</span>
                                            <span className="deadline-date">{formatDate(vehicle.next_inspection)}</span>
                                        </div>
                                        {vehicle.inspection_email && <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '4px' }}>📧 {vehicle.inspection_email}</div>}
                                        {vehicle.workshop_info?.inspection?.company && (
                                            <div style={{ fontSize: '0.8rem', background: 'white', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                                <strong>{vehicle.workshop_info?.inspection?.company}</strong>
                                                {vehicle.workshop_info?.inspection?.phone && <div style={{ fontSize: '0.75rem' }}>📞 {vehicle.workshop_info?.inspection?.phone}</div>}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* UVV */}
                                {vehicle.notification_config?.has_uvv !== false && (
                                    <div className="deadline-card-detail">
                                        <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
                                            <span>UVV / Prüfung</span>
                                            <span className="deadline-date">{formatDate(vehicle.next_tech_check)}</span>
                                        </div>
                                        {vehicle.tech_check_email && <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '4px' }}>📧 {vehicle.tech_check_email}</div>}
                                        {vehicle.workshop_info?.uvv?.company && (
                                            <div style={{ fontSize: '0.8rem', background: 'white', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                                <strong>{vehicle.workshop_info?.uvv?.company}</strong>
                                                {vehicle.workshop_info?.uvv?.phone && <div style={{ fontSize: '0.75rem' }}>📞 {vehicle.workshop_info?.uvv?.phone}</div>}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* CUSTOM DEADLINES */}
                                {(vehicle.notification_config?.custom_deadlines || []).map(cd => (
                                    <div key={cd.id} className="deadline-card-detail" style={{ borderLeft: '3px solid #6366f1' }}>
                                        <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
                                            <span>{cd.name || 'Unbenannte Frist'}</span>
                                            <span className="deadline-date">{formatDate(cd.date)}</span>
                                        </div>
                                        {cd.email && <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '4px' }}>📧 {cd.email}</div>}
                                        {cd.workshop_name && (
                                            <div style={{ fontSize: '0.8rem', background: 'white', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                                <strong>{cd.workshop_name}</strong>
                                            </div>
                                        )}
                                    </div>
                                ))}

                                {/* LOCATION */}
                                <div className="deadline-card-detail">
                                    <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '8px' }}>Standort</div>
                                    <div style={{ fontWeight: 600, color: '#4facfe' }}>{vehicle.location || 'Nicht gelistet'}</div>
                                    {vehicle.location_email && <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px' }}>📧 {vehicle.location_email}</div>}
                                </div>
                            </div>
                        </div>

                        {/* Rechte Spalte: Dokumente & Notizen */}
                        <div>
                            <div className="modal-section-title">🚦 Status & Personal</div>
                            <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                                <div style={{ marginBottom: '12px' }}>
                                    <span className={`status-badge ${vehicle.status}`} style={{ width: '100%' }}>{getStatusLabel(vehicle.status)}</span>
                                </div>
                                <div style={{ fontSize: '0.85rem' }}>
                                    <div style={{ marginBottom: '4px' }}><span style={{ color: '#64748b' }}>Zuständig:</span> <b>{companyUsers.find(u => u.id === vehicle.assigned_user_id)?.name || '—'}</b></div>
                                    <div><span style={{ color: '#64748b' }}>Verantwortlich:</span> <b>{companyUsers.find(u => u.id === vehicle.responsible_user_id)?.name || '—'}</b></div>
                                </div>
                            </div>

                            <div className="modal-section-title">📄 Dokumente</div>
                            <div style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                                <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>📂 {documents.length}</div>
                                <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{documents.length > 0 ? 'Dokumente vorhanden' : 'Keine Dokumente hochgeladen'}</div>
                            </div>
                            
                            {documents.length > 0 && (
                                <div style={{ marginTop: '10px', maxHeight: '150px', overflowY: 'auto' }}>
                                    {documents.map(doc => (
                                        <div key={doc.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: '0.8rem' }}>
                                            <span style={{ fontWeight: 500 }}>{doc.file_name}</span>
                                            <span style={{ color: '#64748b' }}>{formatDate(doc.upload_date)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="modal-section-title" style={{ marginTop: '20px' }}>📝 Notizen</div>
                            <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', fontSize: '0.9rem', color: '#475569', minHeight: '60px' }}>
                                {vehicle.notes || 'Keine Notizen vorhanden.'}
                            </div>
                            
                            {vehicle.status_reason && (
                                <div style={{ marginTop: '15px' }}>
                                    <div style={{ color: '#dc2626', fontWeight: 600, fontSize: '0.8rem', textTransform: 'uppercase', marginBottom: '4px' }}>Defekt-Bericht:</div>
                                    <div style={{ background: '#fee2e2', color: '#991b1b', padding: '12px', borderRadius: '8px', fontSize: '0.85rem' }}>
                                        {vehicle.status_reason}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="modal-footer">
                    <button className="btn-modal-cancel" onClick={onClose}>Schließen</button>
                </div>
            </div>
        </div>
    );
}
